import { useState, useEffect } from "react";
import { KeyRound, LineChart, Radar } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { getSiteSettings, updateGASettings, updateMetaPixelSettings } from "@/admin/adminApi";

const FIELD_CLASS = "w-full mt-1.5 bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-white focus:border-[var(--brand-primary)] outline-none";
const LABEL_CLASS = "text-xs font-mono uppercase tracking-widest text-white/60";

export default function AdminSettingsPage() {
  const { admin, updateProfile } = useAdminAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const [gaId, setGaId] = useState("");
  const [gaEnabled, setGaEnabled] = useState(false);
  const [gaSaving, setGaSaving] = useState(false);
  const [gaSuccess, setGaSuccess] = useState("");

  const [pixelId, setPixelId] = useState("");
  const [pixelEnabled, setPixelEnabled] = useState(false);
  const [pixelSaving, setPixelSaving] = useState(false);
  const [pixelSuccess, setPixelSuccess] = useState("");

  useEffect(() => {
    getSiteSettings().then((s) => {
      setGaId(s.gaMeasurementId || "");
      setGaEnabled(!!s.gaEnabled);
      setPixelId(s.metaPixelId || "");
      setPixelEnabled(!!s.metaPixelEnabled);
    });
  }, []);

  const handleGaSave = async (e) => {
    e.preventDefault();
    setGaSaving(true);
    setGaSuccess("");
    await updateGASettings(gaId.trim() || null, gaEnabled);
    setGaSaving(false);
    setGaSuccess("Google Analytics settings saved.");
  };

  const handlePixelSave = async (e) => {
    e.preventDefault();
    setPixelSaving(true);
    setPixelSuccess("");
    await updateMetaPixelSettings(pixelId.trim() || null, pixelEnabled);
    setPixelSaving(false);
    setPixelSuccess("Meta Pixel settings saved.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!newEmail && !newPassword) {
      setError("Enter a new email and/or a new password to update.");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setSaving(true);
    const result = await updateProfile(currentPassword, newEmail.trim() || null, newPassword || null);
    setSaving(false);

    if (result.ok) {
      setSuccess("Your admin credentials have been updated.");
      setCurrentPassword(""); setNewEmail(""); setNewPassword(""); setConfirmPassword("");
    } else {
      setError(result.error);
    }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-md space-y-10">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Account Settings</h1>
        <p className="text-white/50 text-sm font-mono mb-6">Signed in as {admin?.email}</p>

        {error && <p data-testid="admin-settings-error" className="text-red-400 text-sm mb-4 font-mono">{error}</p>}
        {success && <p data-testid="admin-settings-success" className="text-emerald-400 text-sm mb-4 font-mono">{success}</p>}

        <form onSubmit={handleSubmit} className="space-y-5 border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6">
          <div className="flex items-center gap-2 text-white/80 mb-2">
            <KeyRound size={16} className="text-[var(--brand-primary)]" />
            <span className="text-sm font-semibold">Change Email / Password</span>
          </div>
          <label className="block">
            <span className={LABEL_CLASS}>Current Password (required to confirm changes)</span>
            <input
              data-testid="settings-current-password-input"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={FIELD_CLASS}
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>New Email (optional)</span>
            <input
              data-testid="settings-new-email-input"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className={FIELD_CLASS}
              placeholder="Leave blank to keep current email"
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>New Password (optional, min. 8 characters)</span>
            <input
              data-testid="settings-new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={FIELD_CLASS}
              placeholder="Leave blank to keep current password"
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>Confirm New Password</span>
            <input
              data-testid="settings-confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={FIELD_CLASS}
            />
          </label>
          <button data-testid="settings-save-button" type="submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-50">
            {saving ? "Saving…" : "Update Credentials"}
          </button>
        </form>
      </div>

      <div>
        {gaSuccess && <p data-testid="ga-settings-success" className="text-emerald-400 text-sm mb-4 font-mono">{gaSuccess}</p>}
        <form onSubmit={handleGaSave} className="space-y-5 border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6">
          <div className="flex items-center gap-2 text-white/80 mb-2">
            <LineChart size={16} className="text-[var(--brand-primary)]" />
            <span className="text-sm font-semibold">Google Analytics (GA4)</span>
          </div>
          <p className="text-xs text-white/50 font-mono leading-relaxed">
            Separate from the Analytics dashboard in the nav above (that one is self-hosted and always on).
            This is your REAL Google Analytics Measurement ID — save it now, then switch it on yourself
            the moment this site goes live on the real domain.
          </p>
          <label className="block">
            <span className={LABEL_CLASS}>Measurement ID</span>
            <input
              data-testid="ga-measurement-id-input"
              type="text"
              value={gaId}
              onChange={(e) => setGaId(e.target.value)}
              className={FIELD_CLASS}
              placeholder="G-XXXXXXXXXX"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="ga-enabled-checkbox"
              type="checkbox"
              checked={gaEnabled}
              onChange={(e) => setGaEnabled(e.target.checked)}
              className="accent-[var(--brand-primary)] w-4 h-4"
            />
            <span className="text-sm text-white">Enabled — start sending real traffic to Google Analytics now</span>
          </label>
          <button data-testid="ga-settings-save-button" type="submit" disabled={gaSaving} className="btn-primary w-full justify-center disabled:opacity-50">
            {gaSaving ? "Saving…" : "Save Google Analytics Settings"}
          </button>
        </form>
      </div>

      <div>
        {pixelSuccess && <p data-testid="meta-pixel-settings-success" className="text-emerald-400 text-sm mb-4 font-mono">{pixelSuccess}</p>}
        <form onSubmit={handlePixelSave} className="space-y-5 border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6">
          <div className="flex items-center gap-2 text-white/80 mb-2">
            <Radar size={16} className="text-[var(--brand-primary)]" />
            <span className="text-sm font-semibold">Meta Pixel (Facebook / Instagram)</span>
          </div>
          <p className="text-xs text-white/50 font-mono leading-relaxed">
            Powers retargeting audiences + conversion optimisation for Meta ad campaigns
            (PageView, ViewContent, AddToCart, InitiateCheckout, Purchase, Lead). Find your
            Pixel ID in Meta Events Manager -&gt; Data Sources -&gt; your Pixel -&gt; Settings
            (NOT your Ad Account ID). Save it now, then switch it on yourself whenever you're
            ready — same dormant-until-flipped pattern as Google Analytics above.
          </p>
          <label className="block">
            <span className={LABEL_CLASS}>Pixel ID</span>
            <input
              data-testid="meta-pixel-id-input"
              type="text"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              className={FIELD_CLASS}
              placeholder="e.g. 1234567890123456"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="meta-pixel-enabled-checkbox"
              type="checkbox"
              checked={pixelEnabled}
              onChange={(e) => setPixelEnabled(e.target.checked)}
              className="accent-[var(--brand-primary)] w-4 h-4"
            />
            <span className="text-sm text-white">Enabled — start sending real traffic to Meta Pixel now</span>
          </label>
          <button data-testid="meta-pixel-settings-save-button" type="submit" disabled={pixelSaving} className="btn-primary w-full justify-center disabled:opacity-50">
            {pixelSaving ? "Saving…" : "Save Meta Pixel Settings"}
          </button>
        </form>
      </div>
    </div>
  );
}
